import React, { useState, useEffect, useContext } from 'react';
import cs from 'classnames';
import { extend } from '@thd-nucleus/data-sources';
import { DynamicComponent, AppContext } from '@thd-nucleus/app-render';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { Hydrator } from '@thd-olt-component-react/hydrator';
import {
  Accordion,
  AccordionHeader,
  AccordionBody,
  Skeleton,
  SkeletonContent,
  SkeletonLine,
} from '@one-thd/sui-atomic-components';

import { withBypassComponent } from './withBypassComponent';

const ACCORDION_IDS = {
  ProductOverviewAccordionBody: 'product-section-overview',
  BundleProductDetails: 'product-section-overview',
  SpecificationsAccordionBody: 'product-section-key-feat',
  BundleSpecifications: 'product-section-key-feat',
  SpecificationsWithKPFAccordionBody: 'product-section-key-feat',
  QuestionsAndAnswersAccordionBody: 'product-section-qa',
  RatingsAndReviewsAccordionBody: 'product-section-rr',
};

export function withLayoutAccordion(components, BusinessRules) {
  const Rules = withBypassComponent(BusinessRules, !!BusinessRules);

  function LayoutAccordionComponent(props) {
    const [mounted, setMounted] = useState(false);
    const [expanded, setExpanded] = useState({});
    const [accordionHydrated, setAccordionHydrated] = useState(false);
    const { instance } = useContext(AppContext);
    const thdSeoBotDetected = instance?.thdSEOBotDetection;

    const handleChange = (index) => {
      return (evt, stateVal) => {
        setExpanded((state) => ({
          ...state,
          [index]: typeof stateVal !== 'undefined' ? stateVal : !state[index],
        }));
      };
    };

    const headers = components[0];
    const len = components[0].length;
    const defaultChoices = [
      new Array(len).fill({ index: 0, data: null }),
      new Array(len).fill({ index: 0, data: null }),
    ];
    const {
      fusionSlotName,
      fusionComponentName,
      fusionClassName,
      config,
      slot,
      sduiData,
      queryProvider,
      isDynamic = false,
      ...rest
    } = props;
    let choices = sduiData || defaultChoices;

    const { middleware } = config.slots[slot] || {};

    const triggerAccordionMountEvent = () => {
      if (!choices) return;

      const wrapperIds = headers.map((header, index) => {
        const { export: componentExport } = config.slots[slot].components[1][index].components[
          choices[1][index].index
        ];
        const accordionId = ACCORDION_IDS[componentExport]
          || `${fusionSlotName}-controlled-accordion-${index}-wrapper`;
        return `${accordionId}`;
      });

      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('accordion.mount', {
        wrapperIds,
        setExpanded: handleChange,
      });
    };

    useEffect(() => {
      if (mounted) {
        triggerAccordionMountEvent();
      }
    }, [mounted]);

    useEffect(() => {
      if (accordionHydrated) {
        triggerAccordionMountEvent();
      }
    }, [accordionHydrated]);

    useEffect(() => {
      setMounted(true);
    }, []);

    const bodies = components[1].map((Component, index) => {
      return Component;
    });

    const { fusionClassName: accordionFusionClassName } = config.slots[slot];
    const { dynamicRendering = {} } = config;

    const pageType = Object.keys(dynamicRendering).find(
      (i) => dynamicRendering[i] === true
    );

    const renderAccordion = () => (
      <div
        className={cs(
          'sui-min-h-sm sui-max-w-screen-2xl',
          accordionFusionClassName
        )}
      >
        {bodies.map((Component, index) => {
          const {
            name,
            export: componentExport,
            props: { fusionClassName: bodyFusionClassName = '' },
          } = config.slots[slot].components[1][index].components[
            choices[1][index].index
          ];
          const accordionId = ACCORDION_IDS[componentExport]
            || `${fusionSlotName}-controlled-accordion-${index}-wrapper`;
          const Header = headers[index];
          const headerComponent = config.slots[slot].components[0][index].components[
            choices[0][index].index
          ];
          const headerProps = {
            ...headerComponent.props,
          };
          const bodyComponent = config.slots[slot].components[1][index].components[
            choices[1][index].index
          ];
          const BodyComponent = Array.isArray(Component)
            ? Component[choices[1][index].index]
            : Component;

          const bodyProps = {
            ...bodyComponent.props,
          };

          if (config.slots[slot].isSDUI && bodyProps.hydrator) {
            delete bodyProps.hydrator;
          }

          const HeaderComponent = Array.isArray(Header)
            ? Header[choices[0][index].index]
            : Header;
          return (
            <Rules
              key={index}
              itemId={rest.itemId}
              componentDisplayName={
                HeaderComponent.displayName || Component.name
              }
              middlewareProps={{ ...middleware?.props }}
              props={rest}
            >
              {({ props: additionalProps }) => (
                <div id={`${accordionId}`}>
                  <Accordion
                    key={index}
                    expanded={thdSeoBotDetected || !!expanded[index]}
                    onChange={handleChange(index)}
                  >
                    <AccordionHeader>
                      <HeaderComponent
                        config={config}
                        slot={slot}
                        {...headerProps}
                        {...additionalProps}
                      />
                    </AccordionHeader>
                    <AccordionBody>
                      {(thdSeoBotDetected || !!expanded[index]) && (
                        <BodyComponent
                          hydratorId={`${fusionSlotName}-controlled-body-${index}`}
                          config={config}
                          slot={slot}
                          queryProvider={queryProvider}
                          skipWrapper
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...bodyProps}
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...additionalProps}
                        />
                      )}
                    </AccordionBody>

                  </Accordion>
                </div>
              )}
            </Rules>
          );
        })}
      </div>
    );

    return (
      <div data-fusion-slot={fusionSlotName} data-fusion-component="accordion">
        <ErrorBoundary name="collapsible-sections">
          {isDynamic && (
            <DynamicComponent pageType={pageType}>
              <Hydrator
                id="collapsible-sections-accordion"
                className="sui-flex-wrap sui-flex-auto sui-w-full"
                afterHydrate={() => setAccordionHydrated(true)}
                placeholder={(
                  <div className="sui-flex sui-flex-col sui-w-full sui-gap-2">
                    {bodies.map((Component, index) => {
                      const { export: componentExport } = config.slots[slot].components[1][index].components[
                        choices[1][index].index
                      ];
                      const accordionId = ACCORDION_IDS[componentExport]
                        || `${fusionSlotName}-controlled-accordion-${index}-wrapper`;
                      return (
                        <div
                          id={`${accordionId}`}
                          className="sui-w-full"
                          key={`${componentExport}-${index}`}
                          scroll-behavior="smooth"
                        >
                          <Skeleton grow>
                            <SkeletonContent disablePadding>
                              <SkeletonLine numberOfLines={2} fullWidth />
                            </SkeletonContent>
                          </Skeleton>
                        </div>
                      );
                    })}
                  </div>
                )}
              >
                {renderAccordion()}
              </Hydrator>
            </DynamicComponent>
          )}
          {!isDynamic && <>{renderAccordion()}</>}
        </ErrorBoundary>
      </div>
    );
  }

  const dataModels = components[0]
    .flat()
    .map((titleComponent) => {
      return titleComponent.dataModel || {};
    })
    .concat(components[1].flat())
    .map((bodyComponent) => {
      return bodyComponent.dataModel || {};
    });
  LayoutAccordionComponent.dataModel = extend({}, ...dataModels);

  return LayoutAccordionComponent;
}